import { ReactNode, forwardRef, useMemo } from 'react'
import {
  CrewPopover,
  CrewPopoverProps,
} from 'components/devextreme/crewPopover'
import { Popover } from 'devextreme-react'
import { useTranslation } from '@crew/modules/i18n'

/**
 * resources.tsのlabel.tooltips配下のキー定義
 */
export const ToolTipTypes = {
  usageGuide: 'usageGuide',
  supportPortal: 'supportPortal',
  toggleRightSideBarButton: 'toggleRightSideBarButton',
  aiCreditRemaining: 'aiCreditRemaining',
  toggleAiAssistantButton: 'toggleAiAssistantButton',
  toggleLeftSideBarButton: 'toggleLeftSideBarButton',
  notification: 'notification',
  favorite: 'favorite',
  projectDetailFileDisplayModeList: 'projectDetailFileDisplayModeList',
  projectDetailFileDisplayModeGrid: 'projectDetailFileDisplayModeGrid',
  eventDisplayModeList: 'eventDisplayModeList',
  eventDisplayModeCalendar: 'eventDisplayModeCalendar',
  taskDisplayModeList: 'taskDisplayModeList',
  taskDisplayModeKanban: 'taskDisplayModeKanban',
  taskDisplayModeCalendar: 'taskDisplayModeCalendar',
  taskDisplayModeTimeline: 'taskDisplayModeTimeline',
  taskDisplayModeGantt: 'taskDisplayModeGantt',
  taskDisplayModeEvm: 'taskDisplayModeEvm',
  saveFilterButton: 'saveFilterButton',
  filterSettingsButton: 'filterSettingsButton',
  moveFilesButton: 'moveFilesButton',
  bulkDownloadButton: 'bulkDownloadButton',
  bulkAddTagsButton: 'bulkAddTagsButton',
  bulkDeleteFilesButton: 'bulkDeleteFilesButton',
  // 右上のヘルプボタン、これだけ2項目（ガイド, サポートポータル）表示するので特別
  helpButton: 'helpButton',
  myTaskDisplayModeList: 'myTaskDisplayModeList',
  myTaskDisplayModeCalendar: 'myTaskDisplayModeCalendar',
  myTaskDisplayModeGantt: 'myTaskDisplayModeGantt',
  globalMenuTaskDisplayModeList: 'globalMenuTaskDisplayModeList',
  globalMenuTaskDisplayModeCalendar: 'globalMenuTaskDisplayModeCalendar',
  globalMenuTaskDisplayModeGantt: 'globalMenuTaskDisplayModeGantt',
} as const

export type TooltipTypes = (typeof ToolTipTypes)[keyof typeof ToolTipTypes]

// type, children のいずれかで内容を指定する
// type -> 通常のツールチップ（翻訳キー指定）
// children -> 自由に内容を指定したい場合
export type CrewGuideToolTipProps = Omit<CrewPopoverProps, 'children'> &
  (
    | {
        /**
         * 翻訳キーのタイプ (resources.tsのlabel.tooltips配下のキーと1:1対応)
         *
         * label.tooltips.{type}.full と label.tooltips.{type}.short を参照:
         * - ガイドON: label.tooltips.{type}.full.title / description
         * - ガイドOFF: label.tooltips.{type}.short
         */
        type: TooltipTypes
        children?: never
      }
    | {
        type?: never
        children: ReactNode
      }
  )

export type GuideItem = {
  title: string
  description: string
}

// ガイド用ツールチップ専用のCSSクラス
// クラスの定義はindex.css内
const tooltipPopoverClass = { class: 'crew-guide-tooltip-popover' } as const

export const CrewGuideToolTip = forwardRef<Popover, CrewGuideToolTipProps>(
  (
    {
      target,
      position = 'bottom',
      showEvent = { name: 'mouseenter', delay: 500 },
      hideEvent = 'mouseleave',
      maxWidth = '400',
      type,
      children,
      ...popoverProps
    },
    ref
  ) => {
    const { t } = useTranslation()

    const content = useMemo(() => {
      // children, type の優先順位で判定
      if (children) {
        return children
      }

      if (type) {
        return t(`label.tooltips.${type}`)
      }

      return null
    }, [children, type, t])

    return (
      <CrewPopover
        ref={ref}
        target={target}
        showEvent={showEvent}
        hideEvent={hideEvent}
        position={position}
        maxWidth={maxWidth}
        // ガイド用ツールチップ専用のCSSクラスを指定
        wrapperAttr={tooltipPopoverClass}
        {...popoverProps}
      >
        <div>{content}</div>
      </CrewPopover>
    )
  }
)
